import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Primitives";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Eye, Calendar } from "@phosphor-icons/react";

function formatSchedule(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(iso);
  }
}

function isoToDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(val) {
  if (!val || !String(val).trim()) return null;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function TeacherTests() {
  const [tests, setTests] = useState([]);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [scheduleValue, setScheduleValue] = useState("");
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/tests").then((r) => setTests(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const publish = async (tid) => {
    await api.post(`/tests/${tid}/publish`);
    toast.success("Published");
    load();
  };

  const del = async (tid) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/tests/${tid}`);
    load();
  };

  const openSchedule = (t) => {
    setScheduleFor(t);
    setScheduleValue(isoToDatetimeLocalValue(t.scheduled_at) || "");
  };

  const closeSchedule = () => {
    setScheduleFor(null);
    setScheduleValue("");
  };

  const saveSchedule = async () => {
    if (!scheduleFor) return;
    const iso = datetimeLocalToIso(scheduleValue);
    try {
      await api.patch(`/tests/${scheduleFor.id}/schedule`, { scheduled_at: iso });
      toast.success(iso ? "Schedule saved" : "Schedule cleared");
      closeSchedule();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    }
  };

  const clearScheduleOnly = async () => {
    if (!scheduleFor) return;
    try {
      await api.patch(`/tests/${scheduleFor.id}/schedule`, { scheduled_at: null });
      toast.success("Schedule cleared");
      closeSchedule();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear schedule");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Assessments"
        title="All tests"
        actions={
          <button onClick={() => nav("/teacher/tests/new")} className="btn-primary flex items-center gap-2" data-testid="new-test">
            <Plus size={16} /> Compose paper
          </button>
        }
      />

      {tests.length === 0 ? (
        <EmptyState title="No tests composed" body="Draft a paper using manual selection or AI." />
      ) : (
        <div className="card-academic overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sand">
              <tr>
                <th className="p-4 overline text-left">Title</th>
                <th className="p-4 overline text-left">Type</th>
                <th className="p-4 overline text-left">Class</th>
                <th className="p-4 overline text-left">Section</th>
                <th className="p-4 overline text-left">Marks</th>
                <th className="p-4 overline text-left">Duration</th>
                <th className="p-4 overline text-left">Opens</th>
                <th className="p-4 overline text-left">Closes</th>
                <th className="p-4 overline text-left">Status</th>
                <th className="p-4 overline text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t) => (
                <tr key={t.id} className="border-b border-stone">
                  <td className="p-4 font-medium">{t.title}</td>
                  <td className="p-4 uppercase text-xs font-mono">{t.type}</td>
                  <td className="p-4 text-mossy">{t.class_group}</td>
                  <td className="p-4 text-mossy">{t.section ? t.section : "All"}</td>
                  <td className="p-4 font-mono">{t.total_marks}</td>
                  <td className="p-4 font-mono">{t.duration_minutes}m</td>
                  <td className="p-4 text-mossy text-xs max-w-[10rem]">{formatSchedule(t.scheduled_at)}</td>
                  <td className="p-4 text-mossy text-xs max-w-[10rem]">{formatSchedule(t.scheduled_until)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 text-xs ${t.published ? "bg-forest text-ivory" : "bg-sand text-forest"}`}>
                      {t.published ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="p-4 text-right whitespace-nowrap">
                    {!t.published && (
                      <button onClick={() => publish(t.id)} className="btn-ghost text-xs px-2 py-1.5 mr-1" data-testid={`publish-${t.id}`}>
                        Publish
                      </button>
                    )}
                    <button
                      onClick={() => openSchedule(t)}
                      className="btn-ghost text-xs px-2 py-1.5 inline-flex items-center gap-1 mr-1"
                      data-testid={`schedule-${t.id}`}
                    >
                      <Calendar size={14} /> Schedule
                    </button>
                    <button onClick={() => nav(`/teacher/results?test=${t.id}`)} className="btn-ghost text-xs px-2 py-1.5 mr-1" data-testid={`view-results-${t.id}`}>
                      <Eye size={14} />
                    </button>
                    <button onClick={() => del(t.id)} className="text-mossy hover:text-terracotta inline-flex p-1" data-testid={`del-test-${t.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scheduleFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeSchedule();
          }}
        >
          <div className="bg-ivory border border-stone max-w-md w-full p-6 shadow-lg rounded-sm">
            <h2 id="schedule-title" className="font-serif text-2xl text-forest mb-2">
              Schedule test
            </h2>
            <p className="text-sm text-mossy mb-4">
              After you publish, students only see this test once the open time is reached. Until the <strong>closes</strong> time
              (defaults to max(2× test duration, 30 minutes) after open), they must use <strong>Attempt now</strong> and cannot browse
              the rest of the student panel. Leave empty and save to clear the schedule.
            </p>
            <p className="text-sm font-medium text-forest mb-2">{scheduleFor.title}</p>
            <label className="overline block mb-2">Open date &amp; time (your device timezone)</label>
            <input
              type="datetime-local"
              value={scheduleValue}
              onChange={(e) => setScheduleValue(e.target.value)}
              className="w-full bg-bone border border-stone px-4 py-3 mb-6"
              data-testid="schedule-datetime"
            />
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={clearScheduleOnly} className="btn-ghost text-sm" data-testid="schedule-clear">
                Clear schedule
              </button>
              <button type="button" onClick={closeSchedule} className="btn-ghost text-sm">
                Cancel
              </button>
              <button type="button" onClick={saveSchedule} className="btn-primary text-sm" data-testid="schedule-save">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
